import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FairValueReportCashFlowItem } from '@/services/valuation.service';
import { formatDate, formatCurrency } from '@/utils/helpers';

interface CashflowScheduleProps {
  cashflowData: FairValueReportCashFlowItem[];
  currency?: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
}

export const CashflowSchedule = ({ cashflowData, currency = 'USD' }: CashflowScheduleProps) => {
  const [showAllPeriods, setShowAllPeriods] = useState(false);

  if (!cashflowData) {
    return (
      <Card className="p-6">
        <div className="text-center text-gray-500">
          No cashflow data available for this loan
        </div>
      </Card>
    );
  }

  const displayedCashflows = showAllPeriods
    ? cashflowData
    : cashflowData.slice(0, 12);

  return (
    <>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
            <tr>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Credit Facility Funded Balance
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Credit Facility Draws
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Credit Facility Unfunded Balance
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unfunded Commitment Fee
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Original Balance | Total Committed Line
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Balance - Includes Accrued Interest & PIK Interest
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Interest
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                PIK Interest
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Default Interest
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Principal
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Additional Fees - including Extension & Exit fees
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Equity Kicker
              </th>
              <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cash Flow
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {displayedCashflows.map((cf) => (
              <tr
                key={cf.period}
                className="hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <td className="table-cell px-1 py-2 font-medium text-center">
                  {cf.period}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-center">
                  {formatDate(cf.period_date)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.credit_facility_funded_balance, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.credit_facility_draws, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.credit_facility_unfunded_balance, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.unfunded_commitment_fee, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.original_balance_total_committed_line, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(
                    cf.current_balance_accrued_interest_and_pik_interest,
                    0,
                    currency
                  )}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.interest, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.pik_interest, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.default_interest, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.principal, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.additional_fees, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.equity_kicker, 0, currency)}
                </td>
                <td className="table-cell px-1 py-2 font-medium text-right">
                  {formatCurrency(cf.cash_flow, 0, currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cashflowData.length > 12 && (
        <div className="mt-4 text-center">
          <Button
            onClick={() => setShowAllPeriods(!showAllPeriods)}
            variant="outline"
            size="sm"
            data-test-id="toggle-all-periods"
          >
            {showAllPeriods
              ? `Show Less (12 periods)`
              : `Show All (${cashflowData.length} periods)`}
          </Button>
        </div>
      )}
    </>
  );
};
